import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Bookmark, BookmarkCheck, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useSavedProfiles } from '@/hooks/useSavedProfiles';
import { AuthPromptDialog } from '@/components/AuthPromptDialog';
import { SaveProfileOnboardingDialog } from '@/components/SaveProfileOnboardingDialog';
import { cn } from '@/lib/utils';

interface Props {
  userId: string;
  userName?: string;
  variant?: 'icon' | 'compact' | 'pill';
  className?: string;
}

export const SaveProfileButton = ({ userId, userName, variant = 'icon', className }: Props) => {
  const { user } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [saving, setSaving] = useState(false);
  const { isSaved, toggleSave, markOnboarded } = useSavedProfiles({
    onAuthRequired: () => setShowAuth(true),
    onFirstSave: () => setShowOnboarding(true),
  });

  if (user && user.id === userId) return null;

  const saved = isSaved(userId);
  const Icon = saved ? BookmarkCheck : Bookmark;
  const label = saved ? 'Saved' : 'Save';
  const handleToggle = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await toggleSave(userId, userName);
    } finally {
      setSaving(false);
    }
  };

  if (variant === 'pill') {
    return (
      <>
        <button
          type="button"
          aria-label={saved ? `Unsave ${userName || 'profile'}` : `Save ${userName || 'profile'} for later`}
          title={saved ? 'Remove from Saved' : 'Save for later'}
          disabled={saving}
          aria-busy={saving || undefined}
          onClick={async (e) => {
            e.stopPropagation();
            e.preventDefault();
            await handleToggle();
          }}
          className={cn(
            'inline-flex items-center justify-center rounded-full transition-all shrink-0',
            'h-10 w-10 active:scale-95',
            saved
              ? 'bg-transparent text-primary ring-1 ring-inset ring-primary/30 hover:bg-primary/5'
              : 'bg-primary/5 text-primary hover:bg-primary/10',
            className,
          )}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className={cn('h-4 w-4', saved && 'fill-current')} />}
        </button>

        <SaveProfileOnboardingDialog
          open={showOnboarding}
          onOpenChange={(v) => {
            setShowOnboarding(v);
            if (!v) markOnboarded();
          }}
        />
        <AuthPromptDialog
          open={showAuth}
          onOpenChange={setShowAuth}
          title="Sign in to save profiles"
          description="Create a free account or sign in to save profiles and revisit them later."
        />
      </>
    );
  }

  return (
    <>
      <Button
        type="button"
        variant={saved ? 'secondary' : 'outline'}
        size={variant === 'icon' ? 'icon' : 'sm'}
        aria-label={saved ? `Unsave ${userName || 'profile'}` : `Save ${userName || 'profile'} for later`}
        title={saved ? 'Remove from Saved' : 'Save for later'}
        className={cn(variant === 'icon' && 'h-9 w-9', className)}
        loading={saving}
        disabled={saving}
        onClick={async (e) => {
          e.stopPropagation();
          e.preventDefault();
          await handleToggle();
        }}
      >
        {!saving && <Icon className={cn('h-4 w-4', saved && 'fill-primary text-primary')} />}
        {variant === 'compact' && <span className="ml-1.5 text-xs">{label}</span>}
      </Button>


      <SaveProfileOnboardingDialog
        open={showOnboarding}
        onOpenChange={(v) => {
          setShowOnboarding(v);
          if (!v) markOnboarded();
        }}
      />
      <AuthPromptDialog
        open={showAuth}
        onOpenChange={setShowAuth}
        title="Sign in to save profiles"
        description="Create a free account or sign in to save profiles and revisit them later."
      />
    </>
  );
};
